import { type MemberContactField } from './memberContactEdit';

/** Per-field contact sync status returned by member-operations-service. */
export type ContactFieldSyncStatus = {
  /** Value stored in the onboarding snapshot (FHIR Patient.telecom). */
  snapshot: string | null;
  /** Value currently stored in Cognito (the app login). */
  appLogin: string | null;
  /** Whether Cognito has the value marked verified. */
  verified: boolean;
  /** Whether snapshot and app-login values match. */
  contentInSync: boolean;
  /** Whether the field is fully in sync (content matches and verified). */
  inSync: boolean;
};

export type MemberContactSyncStatus = {
  /** Whether the member has an app (Cognito) login. */
  onboarded: boolean;
  email: ContactFieldSyncStatus;
  phone: ContactFieldSyncStatus;
};

/**
 * The reason a field is out of sync: `content` when the app-login value differs
 * from the snapshot, `unverified` when the values match but Cognito has not
 * marked the value verified. `null` when the field is fully in sync.
 */
export type ContactSyncIssue = 'content' | 'unverified';

export function getContactSyncIssue(
  status: ContactFieldSyncStatus,
): ContactSyncIssue | null {
  if (status.inSync) {
    return null;
  }
  if (!status.contentInSync) {
    return 'content';
  }
  return 'unverified';
}

export const MEMBER_CONTACT_SYNC_QUERY_KEY = ['member-contact-sync'] as const;

export const memberContactSyncStatusPath = (patientId: string): string =>
  `/v1/member-operations/members/${patientId}/contact-sync-status`;

export const syncMemberContactPath = (patientId: string): string =>
  `/v1/member-operations/members/${patientId}/sync-contact`;

/** Fields that support an explicit sync action. */
export const SYNCABLE_CONTACT_FIELDS: MemberContactField[] = ['email', 'phone'];
